import { z } from 'zod';

/** Огласовки (ташкиль) — диапазон арабских диакритических знаков */
const TASHKEEL = /[ً-ْٰ]/;
const ARABIC_LETTERS = /[ء-غف-ي]/;

const sourceSchema = z.object({
  ref: z.string().min(1),
  refRu: z.string().min(1),
  grade: z.enum(['sahih', 'hasan', 'daif', 'quran']).optional(),
});

export const zikrSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'id: только строчные латинские буквы, цифры и дефис'),
  titleRu: z.string().min(1),
  arabic: z
    .string()
    .min(1)
    .refine((s) => ARABIC_LETTERS.test(s), 'arabic: не содержит арабских букв')
    .refine(
      (s) => TASHKEEL.test(s),
      'arabic: нет огласовок — текст должен быть с полным ташкилем',
    ),
  translit: z
    .string()
    .min(1)
    .refine(
      (s) => /[а-яА-ЯёЁ]/.test(s),
      'translit: транскрипция должна быть кириллицей',
    ),
  translationRu: z.string().min(1),
  repeat: z.number().int().positive(),
  source: sourceSchema,
  fadlRu: z.string().optional(),
  fadlSource: sourceSchema.optional(),
});

/**
 * Салават не входит в наборы: у него нет позиции, кратности и завершения.
 * Поэтому отдельный файл, а не запись в azkar.json.
 *
 * text допускает null — это состояние «текст ещё не предоставлен».
 * Пустая строка или текст без огласовок не пройдут: лучше показать
 * счётчик совсем без текста, чем с недоделанным.
 */
export const salawatFileSchema = z.object({
  schemaVersion: z.literal(1),
  text: z
    .object({
      // Арабский обязателен и с полными огласовками — как везде
      arabic: zikrSchema.shape.arabic,
      // Остальное необязательно: владелец может дать один арабский,
      // а транскрипцию и перевод добавить позже. Пустых заглушек тут
      // быть не должно — их пришлось бы сочинять.
      translit: zikrSchema.shape.translit.optional(),
      translationRu: z.string().min(1).optional(),
      source: sourceSchema.optional(),
    })
    .nullable(),
});

export const azkarFileSchema = z.object({
  schemaVersion: z.literal(1),
  /** Пока контент не выверен компетентным человеком — держим true */
  unverified: z.boolean().optional(),
  azkar: z.array(zikrSchema).min(1),
});

export const setManifestSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.enum(['morning', 'evening']),
  titleRu: z.string().min(1),
  subtitleRu: z.string().optional(),
  items: z
    .array(
      z.object({
        zikrId: z.string().min(1),
        repeat: z.number().int().positive().optional(),
      }),
    )
    .min(1),
});

export type AzkarFile = z.infer<typeof azkarFileSchema>;
export type SetManifest = z.infer<typeof setManifestSchema>;
export type SalawatFile = z.infer<typeof salawatFileSchema>;
export type SalawatText = NonNullable<SalawatFile['text']>;

/**
 * Ссылочная целостность: манифест не может ссылаться на несуществующий азкар,
 * а один и тот же азкар не может стоять в наборе дважды.
 * Схема этого не ловит — она проверяет файлы по отдельности.
 */
export function checkReferences(
  azkar: AzkarFile,
  manifests: SetManifest[],
): string[] {
  const errors: string[] = [];
  const known = new Set(azkar.azkar.map((z) => z.id));

  const duplicateIds = azkar.azkar
    .map((z) => z.id)
    .filter((id, i, arr) => arr.indexOf(id) !== i);
  for (const id of new Set(duplicateIds)) {
    errors.push(`azkar.json: дублирующийся id "${id}"`);
  }

  for (const m of manifests) {
    const seen = new Set<string>();
    for (const item of m.items) {
      if (!known.has(item.zikrId)) {
        errors.push(`${m.id}.json: неизвестный zikrId "${item.zikrId}"`);
      }
      if (seen.has(item.zikrId)) {
        errors.push(`${m.id}.json: "${item.zikrId}" встречается дважды`);
      }
      seen.add(item.zikrId);
    }
  }

  const used = new Set(manifests.flatMap((m) => m.items.map((i) => i.zikrId)));
  for (const id of known) {
    if (!used.has(id)) {
      errors.push(`azkar.json: "${id}" не входит ни в один набор`);
    }
  }

  return errors;
}
